// Presentational chrome for the full-page "Upload a book" flow (pressed
// "soft-ink" theme). No app chrome — this is a focused task page with its
// own top bar + centered stepper + close, a centered 620px content column,
// and a sticky action bar. See specs/design_handoff_upload-July8.

import type { ReactNode } from 'react'

import './styles/UploadChrome.css'

export type UploadStepId = 'add' | 'added' | 'details' | 'analyzing' | 'found' | 'building' | 'done'

// The three stepper dots the parent sees. Internal steps collapse onto them.
const STEP_INDEX: Record<UploadStepId, number> = {
  add: 0,
  added: 0,
  details: 1,
  analyzing: 2,
  found: 2,
  building: 2,
  done: 2,
}
const STEP_LABELS = ['Pages', 'Details', 'Ready']

function Check({ size = 12, stroke = '#fff', width = 3.2 }: { size?: number; stroke?: string; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11l4 4 8-9" />
    </svg>
  )
}

function Stepper({ step }: { step: UploadStepId }) {
  const idx = STEP_INDEX[step]
  const done = step === 'done'
  return (
    <div className="upload-flow__stepper">
      {STEP_LABELS.map((label, i) => {
        const isDone = i < idx || (done && i <= idx)
        const isActive = i === idx && !done
        return (
          <div key={label} className="upload-chrome__stepper-item">
            <div className="upload-chrome__stepper-label-group">
              <span className={`upload-chrome__step-number ${isDone ? 'is-done' : isActive ? 'is-active' : 'is-upcoming'}`}>
                {isDone ? <Check /> : i + 1}
              </span>
              <span className={`upload-chrome__step-label ${isActive ? 'is-active' : ''}`}>{label}</span>
            </div>
            {i < 2 && (
              <span className={`upload-chrome__step-line ${i < idx || done ? 'is-complete' : ''}`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

interface UploadShellProps {
  step: UploadStepId
  backLabel: string
  onClose: () => void
  heading?: string
  sub?: string
  center?: boolean
  footer?: ReactNode
  children: ReactNode
}

export function UploadShell({ step, backLabel, onClose, heading, sub, center, footer, children }: UploadShellProps) {
  return (
    <div className="hs-brand upload-chrome">
      {/* top bar — stepper drops to its own row on phone (see
          .upload-flow__topbar in upload-flow.css) so it stops colliding
          with the title + Close button. */}
      <div className="upload-flow__topbar">
        <div className="upload-chrome__brand">
          <span className="upload-chrome__brand-mark">
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15V4M5 9l5-5 5 5M4 16h12" /></svg>
          </span>
          <span className="upload-chrome__brand-title">Upload a book</span>
        </div>
        <Stepper step={step} />
        <div className="upload-chrome__close-slot">
          <button
            type="button" title={`Close and return to ${backLabel}`} onClick={onClose}
            className="hs-press hs-press--paper upload-flow__close-btn"
          >
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15" /></svg>
            <span className="upload-flow__close-label">Close</span>
          </button>
        </div>
      </div>

      {/* content — centered column */}
      <div className={`upload-chrome__content-scroll ${center ? 'upload-chrome__content-scroll--center' : ''}`}>
        <div className={`upload-flow__content-col upload-chrome__content-col ${center ? 'upload-chrome__content-col--center' : ''}`}>
          {heading && (
            <div className="upload-chrome__heading-block">
              <h1 className="upload-flow__heading upload-chrome__heading">{heading}</h1>
              {sub && <p className="upload-chrome__subheading">{sub}</p>}
            </div>
          )}
          {children}
        </div>
      </div>

      {/* sticky action bar — bottom padding respects the phone's home-bar
          inset (safe-area-inset-bottom) instead of sitting flush. */}
      {footer && (
        <div className="upload-chrome__footer">
          <div className="upload-flow__footer-inner upload-chrome__footer-inner">{footer}</div>
        </div>
      )}
    </div>
  )
}

// ── footer buttons ──────────────────────────────────────────────────────────

const arrowR = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h11M11 5l5 5-5 5" /></svg>
)

export function GhostBtn({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className="upload-chrome__ghost-btn">
      {children}
    </button>
  )
}

export function PaperBtn({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className="hs-press hs-press--paper upload-chrome__paper-btn">
      {children}
    </button>
  )
}

export function CoralBtn({ children, onClick, disabled, arrow }: { children: ReactNode; onClick?: () => void; disabled?: boolean; arrow?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`upload-chrome__coral-btn ${disabled ? 'is-disabled' : 'hs-press'}`}
    >
      {children}{arrow && arrowR}
    </button>
  )
}

// ── the fewer-is-better guidance tip ────────────────────────────────────────

export function FewerTip() {
  return (
    <div className="upload-chrome__tip">
      <span className="upload-chrome__tip-icon">
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2.5a5 5 0 0 0-3 9v2h6v-2a5 5 0 0 0-3-9zM8 16.5h4M8.5 18.5h3" /></svg>
      </span>
      <div className="upload-chrome__tip-copy">
        <strong className="upload-chrome__tip-strong">You don&rsquo;t need the whole book.</strong> A chapter, or about 5 to 10 pages, is plenty. We only read the text, not the pictures, so photos don&rsquo;t need to be perfect. Just make sure the words are legible.
      </div>
    </div>
  )
}

// ── a labeled pressed field ─────────────────────────────────────────────────

export function Field({ label, value, onChange, placeholder, optional }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; optional?: boolean }) {
  return (
    <div className="upload-chrome__field">
      <label className="upload-chrome__field-label">
        {label}{optional && <span className="upload-chrome__field-optional"> · optional</span>}
      </label>
      <input
        type="text" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="upload-chrome__field-input"
      />
    </div>
  )
}
